import React from 'react';
import PropTypes from 'prop-types';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Posts from '../../components/Posts/Posts';
import CTASimple from '../../components/CTASimple/CTASimple';
import theme from '../../styles/theme';
import HeroOverlap from '../../components/HeroOverlap/HeroOverlap';
import ButtonLink from '../../components/Shared/ButtonLink';
import Accordion from '../../components/Accordion/Accordion';
import Error from '../../components/Error/Error';
import { getPageContent } from '../../services/dpc';
import ReactMarkdown from 'react-markdown';
import useGTM from '../../hooks/useGTM';
import { slugByLabel } from '../../util/faqCategories';
import createGTMClass from '../../util/createGTMClass';
import { getBusinessCardsForTeam } from '../../services/businessCard';
import getMainContentType, {
  types,
} from '../../util/contentful/getMainContentType';
import LicenseMap from '../../components/LicenseMap/TeamLicenseMap';
import { getHexValuesInString } from '../../util/colors';

const faqGTMLabels = {
  'what-is-an-advance': 'advance',
  'after-my-loan-is-paid-off-when-will-i-get-my-documents': 'payoff_docs',
  'difference-between-current-principal-balance-and-payoff-quote':
    'principle_payoff',
};

const PortfolioMortgage = ({ data = {}, error, isLoading, className = '' }) => {
  const {
    contentCTAs,
    contentVideos,
    faqPod,
    heroCta,
    heroMedia,
    mobileHeroMedia,
    metaDescription,
    preFooterCta,
    tipsAndToolsPod,
    titleTag,
    customerReviewPod,
  } = data;

  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();
  const faqs =
    faqPod &&
    faqPod.map(
      ({
        faqQuestion: title,
        faqSummaryAnswer: answer,
        slug,
        faqCategory,
        ...rest
      }) => ({
        title,
        answer,
        showReadMore:
          rest.faqAnswerContent1 ||
          rest.faqAnswerContent2 ||
          rest.faqAnswerVideos,
        slug: slug.replace(
          'resources/faqs/',
          `resources/faqs/${slugByLabel(faqCategory?.[0])}/`
        ),
        onExpand: triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Portfolio Home Loan',
          category: 'frequently_asked',
          action: 'summary',
          label:
            faqGTMLabels[
              slug.replace('/resources/faqs/', '').replace(/\/$/, '')
            ] ||
            slug
              .replace('/resources/faqs/', '')
              .replace(/-/g, '_')
              .replace(/\/$/, ''),
        }),
        onReadMore: triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Portfolio Home Loan',
          category: 'frequently_asked',
          action: 'text',
          label:
            faqGTMLabels[
              slug.replace('/resources/faqs/', '').replace(/\/$/, '')
            ] ||
            slug
              .replace('/resources/faqs/', '')
              .replace('-', '_')
              .replace(/\/$/, ''),
        }),
      })
    );

  const posts =
    tipsAndToolsPod &&
    tipsAndToolsPod.map(
      ({ title, contentTag, category, slug, featuredMedia }) => ({
        title,
        category,
        contentTag,
        thumbnail: `${featuredMedia?.media?.fields?.file?.url}?w=300`,
        slug,
      })
    );

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />

      <HeroOverlap
        position='left'
        background={heroMedia?.[0]?.media?.fields?.file?.url}
        mobileBackground={mobileHeroMedia?.[0]?.media?.fields?.file?.url}
        alt={heroMedia?.[0]?.altText}
      >
        <h1>{heroCta?.ctaHeader}</h1>
        <ReactMarkdown source={heroCta?.ctaIntro} />
        <section className='links'>
          <ButtonLink
            appearance='dark'
            to={heroCta?.firstCTAButtonUrl}
            isExternal={true}
            newTab={true}
            size='large'
            className={createGTMClass('apply', 'hero', 'button')}
            onClick={triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Portfolio Home Loan',
              category: 'loan_application',
              action: 'button',
              label: 'apply',
            })}
          >
            {heroCta?.firstCTAButtonText}
          </ButtonLink>
        </section>
      </HeroOverlap>

      {/* MAP SECTION */}

      <div className='map-section'>
        {data?.mainContent?.map((content, index) => {
          const contentType = getMainContentType(content);

          switch (contentType) {
            case types.INTERACTIVE_MAP:
              return (
                <LicenseMap
                  key={`interactive-map-${content?.contentfulName}-${index}`}
                  modalGreeting={content?.teamMembers?.modalGreeting}
                  businessCards={content?.teamMembers?.businessCards}
                  loanOfficerListHeading={content?.teamMembers?.sectionHeader}
                  mapHeading={content?.mapSectionHeader}
                  mapBackgroundColor={
                    getHexValuesInString(content?.mapBackgroundColor)[0]
                  }
                  licensedStateColor={
                    getHexValuesInString(content?.licensedStateColor)[0]
                  }
                  selectedStateColor={
                    getHexValuesInString(content?.selectedStateColor)[0]
                  }
                  displayLicenseNumbersInPicker={false}
                />
              );
            default:
              return <></>;
          }
        })}
      </div>

      <Accordion
        items={faqs}
        link={{
          url: '/resources/faqs/',
          label: 'View all',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Portfolio Home Loan',
            category: 'frequently_asked',
            action: 'text',
            label: 'faqs',
          }),
        }}
      />

      <CTASimple
        title={contentCTAs?.[0]?.ctaHeader}
        link={{
          title: contentCTAs?.[0].firstCTAButtonText || 'Estimate Payment',
          to:
            contentCTAs?.[0]?.firstCTAButtonUrl ||
            '/calculators/mortgage-calculator/',
          size: 'large',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Portfolio Home Loan',
            category: 'loan_evaluation',
            action: 'button',
            label: 'mortgage_calc',
          }),
        }}
        condensed={true}
        borders={true}
      >
        <p>{contentCTAs?.[0]?.ctaIntro}</p>
      </CTASimple>

      <Posts
        title='Tips & Tools'
        link={{ title: 'View all', to: '/resources/blog/' }}
        posts={posts}
      />

      <CTASimple
        title={preFooterCta?.ctaHeader || 'We Can Help You Get Started'}
        link={{
          to: preFooterCta?.firstCTAButtonUrl || '/applynow/welcome/',
          title: preFooterCta?.firstCTAButtonText || 'Apply Today',
          isExternal: true,
          newTab: true,
          size: 'large',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Portfolio Home Loan',
            category: 'loan_application',
            action: 'button',
            label: 'apply',
          }),
          className: createGTMClass('apply', 'prefoot_cta', 'button'),
        }}
      ></CTASimple>
    </Layout>
  );
};

PortfolioMortgage.getInitialProps = async ({}) => {
  if (typeof window !== 'undefined') {
    window.location.reload();
  } else {
    try {
      const data = await getPageContent(
        '/manufactured-home-loans/new-manufactured-home-loan/'
      );
      const businessCards = await getBusinessCardsForTeam('Portfolio Lending');

      if (data.status !== 200) {
        throw data.statusText;
      }

      const modifiedData = {
        ...data.data.result,
        mainContent: data.data.result.mainContent.map(v => {
          const isLicenseMap = getMainContentType(v) === types.INTERACTIVE_MAP;

          if (isLicenseMap) {
            return {
              ...v,
              teamMembers: {
                ...v.teamMembers,
                businessCards: v.teamMembers.businessCards.reduce((p, c, i) => {
                  const foundCard = businessCards.find(f => f.uid === c.uid);
                  if (foundCard) {
                    c.stateLicenses = foundCard.stateLicenses;
                    p.push(c);
                  }

                  return p;
                }, []),
              },
            };
          }

          return v;
        }),
      };

      return {
        data: modifiedData,
      };
    } catch (error) {
      return {
        error,
      };
    }
  }
};

PortfolioMortgage.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default PortfolioMortgage;
